import type { Language } from "./i18n";

export const LANGUAGES: Language[] = ["de", "en", "it", "es", "nl"];

export const LANGUAGE_LABELS: Record<Language, string> = {
  de: "Deutsch",
  en: "English",
  it: "Italiano",
  es: "Español",
  nl: "Nederlands",
};

/** Static route segments per language */
export const routePaths = {
  home:       { de: "",         en: "",             it: "",           es: "",           nl: ""           },
  about:      { de: "agentur",  en: "agency",       it: "agenzia",    es: "agencia",    nl: "agentschap" },
  products:   { de: "sortiment",en: "portfolio",    it: "catalogo",   es: "catalogo",   nl: "assortiment"},
  contact:    { de: "kontakt",  en: "contact",      it: "contatto",   es: "contacto",   nl: "contact"    },
  manufacturers:{ de: "hersteller", en: "manufacturers", it: "produttori", es: "fabricantes", nl: "fabrikanten" },
  impressum:  { de: "impressum",en: "imprint",      it: "impronta",   es: "aviso-legal",nl: "colofon"    },
  datenschutz:{ de: "datenschutz",en: "privacy-policy", it: "informativa-privacy", es: "politica-privacidad", nl: "privacybeleid" },
  cookiePolicy:{ de: "cookie-richtlinie", en: "cookie-policy", it: "cookie-policy", es: "politica-cookies", nl: "cookiebeleid" },
  terms:      { de: "nutzungsbedingungen", en: "terms-of-service", it: "termini-di-servizio", es: "terminos-de-servicio", nl: "gebruiksvoorwaarden" },
  productsPrefix: { de: "produkte", en: "products", it: "prodotti",   es: "productos",  nl: "producten"  },
} as const;

export type RouteKey = keyof typeof routePaths;

/** Generate a localized path: p("about", "de") → "/de/agentur" */
export function localePath(key: RouteKey, lang: Language, suffix?: string): string {
  const segment = routePaths[key][lang];
  const base = `/${lang}`;
  if (!segment) return suffix ? `${base}/${suffix}` : base;
  return suffix ? `${base}/${segment}/${suffix}` : `${base}/${segment}`;
}

/** Translate current path from one language to another */
export function translatePath(pathname: string, fromLang: Language, toLang: Language): string {
  // Strip lang prefix
  const rest = pathname.replace(new RegExp(`^/${fromLang}(/|$)`), "/").replace(/^\/+/, "/");

  for (const key of Object.keys(routePaths) as RouteKey[]) {
    const fromSegment = routePaths[key][fromLang];
    const toSegment = routePaths[key][toLang];
    if (!fromSegment) continue;

    // Exact match
    if (rest === `/${fromSegment}` || rest === `/${fromSegment}/`) {
      return `/${toLang}/${toSegment}`;
    }
    // Prefix match (for dynamic sub-routes like /hersteller/:slug)
    if (rest.startsWith(`/${fromSegment}/`)) {
      const remainder = rest.slice(`/${fromSegment}`.length);
      return `/${toLang}/${toSegment}${remainder}`;
    }
  }

  // Fallback: keep rest with new lang prefix
  return rest === "/" ? `/${toLang}` : `/${toLang}${rest}`;
}

/** Detect best language from browser settings */
export function detectBrowserLanguage(): Language {
  if (typeof navigator === "undefined") return "en";
  const browserLangs = navigator.languages || [navigator.language];
  for (const bl of browserLangs) {
    const code = bl.toLowerCase().split("-")[0];
    if (code === "de") return "de";
    if (code === "en") return "en";
    if (code === "it") return "it";
    if (code === "es") return "es";
    if (code === "nl") return "nl";
  }
  return "en"; // International fallback
}
